import { savePeoplePage } from "apps/project-iq/api";
import { useLinks } from "apps/project-iq/hooks/useLinks";
import { DEFAULT_TEAM_ROLES } from "apps/project-iq/constants/defaultPeopleData";
import {
  IStakeholder,
  ITeamRole,
  IWorkspacePeople,
} from "models/IProjectIQWorkspace";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  selectAvailableProjectIqWorkspaces,
  updateProjectIqWorkspace as updateWorkspaceAction,
} from "redux/projectIqWorkspacesSlice";
import { StakeholdersSection } from "./StakeholdersSection";
import { TeamRolesSection } from "./TeamRolesSection";
import {
  SAVE_DEBOUNCE_MS,
  SAVE_IDLE_DELAY_MS,
} from "./WorkspacePeople.constants";
import { useWorkspacePeopleStyles } from "./WorkspacePeople.styles";
import {
  IWorkspacePeopleProps,
  PeopleSaveStatus,
} from "./WorkspacePeople.types";

export const WorkspacePeople = ({
  workspaceIdentifier,
  onSaveStatusChange,
}: IWorkspacePeopleProps): JSX.Element => {
  const styles = useWorkspacePeopleStyles();
  const { links: peopleLinks, isLoading: isLinksLoading } = useLinks("people");
  const dispatch = useDispatch();
  const workspaces = useSelector(selectAvailableProjectIqWorkspaces);
  const workspace = workspaces.find((w) => w.id === workspaceIdentifier);
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const canEdit =
    workspace?.owners?.some((owner) => owner.userId === currentUser?.id) ??
    false;

  const [people, setPeople] = useState<IWorkspacePeople>({
    teamRoles: [...DEFAULT_TEAM_ROLES],
    stakeholders: [],
  });
  const [saveStatus, setSaveStatus] = useState<PeopleSaveStatus>("idle");
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onSaveStatusChange?.(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  useEffect(() => {
    const workspacePeople = workspace?.people as IWorkspacePeople | undefined;
    if (!workspacePeople) return;
    setPeople({
      teamRoles: workspacePeople.teamRoles?.length
        ? workspacePeople.teamRoles
        : [...DEFAULT_TEAM_ROLES],
      stakeholders: workspacePeople.stakeholders ?? [],
    });
  }, [workspace?.id]);

  const save = async (next: IWorkspacePeople) => {
    if (!workspace || !canEdit) return;
    setSaveStatus("saving");
    try {
      await savePeoplePage({ id: workspaceIdentifier, people: next });
      dispatch(updateWorkspaceAction({ ...workspace, people: next }));
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), SAVE_IDLE_DELAY_MS);
    } catch {
      setSaveStatus("error");
    }
  };

  const scheduleSave = (next: IWorkspacePeople) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => void save(next), SAVE_DEBOUNCE_MS);
  };

  const handleTeamRolesChange = (nextRoles: ITeamRole[]) => {
    const next = { ...people, teamRoles: nextRoles };
    setPeople(next);
    scheduleSave(next);
  };

  const handleStakeholdersChange = (nextStakeholders: IStakeholder[]) => {
    const next = { ...people, stakeholders: nextStakeholders };
    setPeople(next);
    scheduleSave(next);
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageContent}>
        <div className={styles.root}>
          <TeamRolesSection
            teamRoles={people.teamRoles}
            members={[
              ...(workspace?.owners ?? []),
              ...(workspace?.teamMembers ?? []),
            ]}
            canEdit={canEdit}
            onRolesChange={handleTeamRolesChange}
            peopleLinks={peopleLinks}
            isLinksLoading={isLinksLoading}
          />
          <StakeholdersSection
            stakeholders={people.stakeholders}
            canEdit={canEdit}
            onStakeholdersChange={handleStakeholdersChange}
          />
        </div>
      </div>
    </div>
  );
};
